import React, { useState, useEffect } from "react";
import "../style/css/UserProfile.css";
import api from "../api.js";
import { useNavigate } from "react-router-dom";

// The main structure for the entire user profile data
export interface ProfileData {
  // Header Info
  name: string;
  username: string;
  title: string;
  avatarUrl: string;

  // Personal Info
  email: string;
  phone: string;
  bio: string;

  // Specialties and Services
  specialties: string[];
  services: string[];

  schedule: any[];
  permissions: string[];
  settings: {
    language: string;
    timezone: string;
    notifications: boolean;
  };
}

const UserProfile: React.FC = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = api.get("/profiles/");
        const data: ProfileData = (await response).data;
        setProfile(data);
      } catch (err) {
        setError(
          "⚠️ Error al cargar el perfil. No pudimos obtener la información.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  if (loading) {
    return (
      <div
        className="container text-center"
        style={{ padding: "var(--space-xl) 0" }}
      >
        <p>Cargando perfil...</p>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div
        className="container text-center"
        style={{ padding: "var(--space-xl) 0" }}
      >
        <h2 style={{ color: "var(--error)", marginBottom: "var(--space-sm)" }}>
          Error de Carga
        </h2>
        <p>{error || "No se encontró el perfil."}</p>
        <a href="/" className="btn btn--primary">
          Volver a Inicio
        </a>
      </div>
    );
  }

  const {
    name,
    username,
    title,
    avatarUrl,
    email,
    phone,
    bio,
    specialties,
    services,
  } = profile;

  return (
    <div className="container">
      <div className="UserProfile-layout">
        {/* === PROFILE HEADER === */}
        <div className="UserProfile-header">
          <img
            src={avatarUrl || "user_placeholder.svg"}
            className="UserProfile-avatar"
            alt="User avatar"
          />
          <div className="UserProfile-info">
            <h1 className="UserProfile-name">{name}</h1>
            <p className="UserProfile-username">@{username}</p>
            <p className="UserProfile-title">{title}</p>
          </div>

          <div className="UserProfile-actions">
            <button
              className="btn btn--secondary"
              onClick={() => navigate("/profile/edit")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              Editar Perfil
            </button>
          </div>
        </div>

        {/* === PERSONAL INFORMATION CARD === */}
        <div className="UserProfile-card">
          <div className="UserProfile-card-header">
            <h2>Información Personal</h2>
          </div>

          <div className="UserProfile-info-group">
            <span className="UserProfile-info-label">Nombre Completo</span>
            <div className="UserProfile-info-value">{name}</div>
          </div>

          <div className="UserProfile-info-group">
            <span className="UserProfile-info-label">Título Profesional</span>
            <div className="UserProfile-info-value">{title}</div>
          </div>

          <div className="UserProfile-info-group">
            <span className="UserProfile-info-label">Correo Electrónico</span>
            <div className="UserProfile-info-value">{email}</div>
          </div>

          <div className="UserProfile-info-group">
            <span className="UserProfile-info-label">Teléfono</span>
            <div className="UserProfile-info-value">{phone}</div>
          </div>

          <div className="UserProfile-info-group">
            <span className="UserProfile-info-label">
              Biografía Profesional
            </span>
            <div className="UserProfile-info-value">{bio}</div>
          </div>
        </div>

        {/* === SPECIALTIES AND SERVICES CARD === */}
        <div className="UserProfile-card">
          <div className="UserProfile-card-header">
            <h2>Especialidades y Servicios</h2>
          </div>

          <div className="UserProfile-info-group">
            <span className="UserProfile-info-label">Mis Especialidades</span>
            <div className="UserProfile-specialties-container">
              {specialties.map((specialty, index) => (
                <div key={index} className="UserProfile-specialty-tag">
                  {specialty}
                </div>
              ))}
            </div>
          </div>

          <div className="UserProfile-info-group">
            <span className="UserProfile-info-label">Servicios Ofrecidos</span>
            <div className="UserProfile-specialties-container">
              {services.map((service, index) => (
                <div key={index} className="UserProfile-specialty-tag">
                  {service}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
